import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { Truck, Plus, Search, Edit, Trash2, Loader2, ArrowUpDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CarriersPage = () => {
  const { token } = useAuth();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    email: '',
    phone: '',
    fleet_size: 0,
    active: true
  });

  useEffect(() => {
    fetchCarriers();
  }, [token]);

  const fetchCarriers = async () => {
    try {
      const data = await api.get('/carriers', token);
      setCarriers(data);
    } catch (error) {
      toast.error('Erro ao carregar transportadores');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCarrier) {
        await api.put(`/carriers/${editingCarrier.id}`, formData, token);
        toast.success('Transportador atualizado');
      } else {
        await api.post('/carriers', formData, token);
        toast.success('Transportador criado');
      }
      setDialogOpen(false);
      resetForm();
      fetchCarriers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este transportador?')) return;
    try {
      await api.delete(`/carriers/${id}`, token);
      toast.success('Transportador removido');
      fetchCarriers();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const openEditDialog = (carrier) => {
    setEditingCarrier(carrier);
    setFormData({
      name: carrier.name,
      cnpj: carrier.cnpj,
      email: carrier.email,
      phone: carrier.phone,
      fleet_size: carrier.fleet_size,
      active: carrier.active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCarrier(null);
    setFormData({ name: '', cnpj: '', email: '', phone: '', fleet_size: 0, active: true });
  };

  const filteredCarriers = carriers
    .filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      if (sortBy === 'created_desc') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'created_asc') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="carriers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pl-14">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Public Sans' }}>
            Transportadores
          </h1>
          <p className="text-slate-500 mt-1">{carriers.length} transportadores cadastrados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-carrier-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Transportador
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Public Sans' }}>
                {editingCarrier ? 'Editar Transportador' : 'Novo Transportador'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="carrier-name-input" />
              </div>
              <div className="space-y-2">
                <Label>CNPJ</Label>
                <Input value={formData.cnpj} onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })} required data-testid="carrier-cnpj-input" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="carrier-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required data-testid="carrier-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Tamanho da Frota</Label>
                <Input type="number" value={formData.fleet_size} onChange={(e) => setFormData({ ...formData, fleet_size: parseInt(e.target.value) })} data-testid="carrier-fleet-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.active} onCheckedChange={(v) => setFormData({ ...formData, active: v })} data-testid="carrier-active-switch" />
                <Label>Ativo</Label>
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-carrier-btn">
                  {editingCarrier ? 'Atualizar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Buscar transportadores..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-carriers-input" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-52" data-testid="sort-carriers">
                <ArrowUpDown className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Nome (A-Z)</SelectItem>
                <SelectItem value="name_desc">Nome (Z-A)</SelectItem>
                <SelectItem value="created_desc">Cadastro (mais recente)</SelectItem>
                <SelectItem value="created_asc">Cadastro (mais antigo)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>CNPJ</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Frota</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCarriers.map((carrier) => (
                  <tr key={carrier.id} data-testid={`carrier-row-${carrier.id}`}>
                    <td className="font-medium text-slate-900">{carrier.name}</td>
                    <td className="text-slate-600 font-mono text-sm">{carrier.cnpj}</td>
                    <td className="text-slate-600">{carrier.email}</td>
                    <td className="text-slate-600">{carrier.phone}</td>
                    <td className="text-slate-600 font-mono">{carrier.fleet_size}</td>
                    <td>
                      <Badge className={carrier.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {carrier.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(carrier)} data-testid={`edit-carrier-${carrier.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(carrier.id)} data-testid={`delete-carrier-${carrier.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default CarriersPage;
